import type { ExerciseId, MoveFlag, MoveTag } from "./types";

/**
 * Annotations on the exercise library that the original PWA never carried.
 *
 * They live here rather than in `data/exercises.ts` because that file is generated from the
 * reference PWA (ADR-001) and is silently overwritten by the next extractor run. Keeping the
 * additions in `domain/` means they survive a regeneration, and can be unit-tested — which is how
 * the missing chest-opener flag below was pinned.
 */

/* ---------- sidedness ---------- */

/**
 * Moves held on one side at a time that the plan does not already mark `side: true`.
 *
 * `buildQueue` unions this with the plan's own flag, and for a *timed* move that means running the
 * dose twice, once per side — a 30-second stretch is 30 seconds per shoulder, not fifteen. That is
 * how the plan already schedules every other one-sided hold, and the doses prove it: the wood chop
 * is 20 seconds where its neighbours in the same block are 40, precisely because the flag doubles
 * it.
 *
 * A full audit of all 48 moves found exactly one gap. The doorway chest opener is one forearm on
 * one frame, yet it sat in the shared cool-down with a single 30-second slot, so one shoulder was
 * stretched and the other never was, and the player offered no moment at which to switch.
 *
 * The other one-sided holds — side plank, wood chop, supine twist, figure-4, kneeling hip flexor,
 * thread the needle, knee-to-chest — are already flagged in the plan and must NOT be listed here.
 *
 * Moves that alternate sides *within* a set are deliberately absent: a dead bug or a bicycle
 * crunch switches as you go, so one dose already covers both sides and doubling it would double
 * the work rather than balance it.
 */
export const TWO_SIDED: ReadonlySet<ExerciseId> = new Set<ExerciseId>(["chestopen"]);

/** True when a move should be run left then right, from either the plan or the correction above. */
export function isTwoSided(id: ExerciseId, flagged?: boolean): boolean {
  return !!flagged || TWO_SIDED.has(id);
}

/* ---------- targeted area ---------- */

/**
 * What each move is actually working, in words someone who does not lift would recognise.
 *
 * The library already says *where* a move is performed ("bed", "standing") and what *kind* it is
 * ("core", "mobility"), but never what it trains — so two moves that feel similar gave no clue
 * that one is for the obliques and the other for the hip flexors. The vocabulary is deliberately
 * small and reused, so the same target reads identically everywhere it appears.
 */
export const TARGET_AREA: Readonly<Record<ExerciseId, string>> = {
  armcircles: "Shoulders",
  twists: "Waist & spine",
  catcow: "Spine",
  hipcircles: "Hips",
  march: "Legs & heart",
  wallpush: "Chest & triceps",
  deadbug: "Deep core",
  inclinepush: "Chest & triceps",
  bridge: "Glutes & hamstrings",
  sideplank: "Obliques",
  bicycle: "Obliques",
  plank: "Whole core",
  stdoblique: "Obliques",
  yraise: "Upper back",
  marchdrive: "Legs & heart",
  boxing: "Shoulders & waist",
  sidebend: "Obliques",
  wallpushfast: "Chest & triceps",
  woodchop: "Obliques",
  kneetwist: "Obliques",
  punchup: "Shoulders",
  steptouch: "Legs & heart",
  hollow: "Deep core",
  birddog: "Core & lower back",
  revcrunch: "Lower abs",
  heeltaps: "Obliques",
  bridgehold: "Glutes",
  squat: "Thighs & glutes",
  revlunge: "Thighs & glutes",
  squatpulse: "Thighs & glutes",
  pelvictilt: "Lower back & belly",
  supinetwist: "Lower back & waist",
  breathing: "Belly & breathing",
  legsup: "Legs & lower back",
  happybaby: "Hips & inner thighs",
  butterfly: "Inner thighs & hips",
  recbutterfly: "Inner thighs & hips",
  sphinx: "Lower back & chest",
  widechild: "Lower back & hips",
  seatedfold: "Hamstrings & lower back",
  fig4: "Glutes & hips",
  wipers: "Lower back & hips",
  hipflexor: "Hip flexors",
  thread: "Upper back & shoulders",
  wallangels: "Upper back & shoulders",
  childs: "Lower back & hips",
  kneechest: "Lower back & glutes",
  chestopen: "Chest & shoulders",

  // the move library's 58 (ADR-051)
  neckturn: "Neck",
  chintuck: "Neck & posture",
  shroll: "Shoulders",
  armraise: "Shoulders",
  ankle: "Ankles",
  trunkturn: "Waist & spine",
  backext: "Upper back",
  seatmarch: "Legs & heart",
  sitstand: "Thighs & glutes",
  minisquat: "Thighs & glutes",
  calfraise: "Calves",
  toeraise: "Shins",
  sideleg: "Outer hips",
  kneecurl: "Hamstrings",
  kneeext: "Thighs",
  quadset: "Thighs",
  slr: "Thighs",
  stepup: "Thighs & glutes",
  wallsit: "Thighs",
  clam: "Outer hips",
  hipext: "Glutes",
  heelslide: "Hips & knees",
  heeldig: "Legs & heart",
  weightshift: "Balance",
  semitandem: "Balance",
  tandemstand: "Balance",
  onelegstand: "Balance",
  tandemwalk: "Balance",
  sidewalk: "Balance & hips",
  grapevine: "Balance & hips",
  backwalk: "Balance",
  figure8: "Balance",
  heeltoewalk: "Shins & calves",
  tree: "Balance",
  tadasana: "Balance",
  armcurl: "Arms",
  press: "Shoulders",
  row: "Upper back",
  scapsqueeze: "Upper back",
  wallslide: "Shoulders",
  pendulum: "Shoulders",
  towelreach: "Shoulders",
  towelsqueeze: "Grip & forearms",
  wallplank: "Whole core",
  wallside: "Obliques",
  standbirddog: "Core & lower back",
  curlup: "Deep core",
  seatpunch: "Shoulders & waist",
  seatjack: "Legs & heart",
  walk: "Legs & heart",
  calfstretch: "Calves",
  hamchair: "Hamstrings",
  standhipflex: "Hip flexors",
  seatfig4: "Glutes & hips",
  sidebendchair: "Obliques",
  anulom: "Breathing",
  bhramari: "Breathing",
  shavasana: "Breathing & rest",

  // the morning yoga and breathing session's own (ADR-067)
  chairtadasana: "Shoulders",
  foldchair: "Hamstrings & lower back",
  catcowchair: "Spine",
  kapalbhati: "Breathing",
};

/** The target area for a move, or null for one the table has not caught up with yet. */
export function targetArea(id: ExerciseId): string | null {
  return TARGET_AREA[id] ?? null;
}

/* ---------- the move library's annotations on the reference's 48 (ADR-051) ---------- */

/**
 * Where each move happens and what it loads, in the move library's vocabulary (`MoveTag`). Exactly
 * one position tag each. "Standing" here is free-standing; the reference has no supported moves.
 * The incline push-up is on the bed's edge, the kneeling hip-flexor on a cushion: both count as bed.
 */
export const TAGS: Readonly<Record<ExerciseId, MoveTag[]>> = {
  armcircles: ["standing", "shoulder"],
  twists: ["standing", "rotation"],
  catcow: ["bed", "wrist", "knee", "flexion"],
  hipcircles: ["standing", "hip"],
  march: ["standing", "hip", "knee"],
  wallpush: ["wall", "shoulder", "wrist"],
  deadbug: ["bed", "hip"],
  inclinepush: ["bed", "shoulder", "wrist"],
  bridge: ["bed", "hip"],
  squat: ["standing", "knee", "hip", "balance"],
  sideplank: ["bed", "shoulder", "knee", "isometric"],
  bicycle: ["bed", "flexion", "rotation", "neck"],
  plank: ["bed", "shoulder", "isometric"],
  stdoblique: ["standing", "neck", "balance"],
  yraise: ["bed", "neck", "shoulder"],
  marchdrive: ["standing", "hip", "knee"],
  boxing: ["standing", "shoulder", "rotation", "impact"],
  sidebend: ["standing", "shoulder"],
  wallpushfast: ["wall", "shoulder", "wrist"],
  woodchop: ["standing", "flexion", "rotation", "knee"],
  kneetwist: ["standing", "hip", "rotation", "balance"],
  punchup: ["standing", "shoulder"],
  steptouch: ["standing", "hip", "knee"],
  hollow: ["bed", "flexion", "neck", "isometric"],
  birddog: ["bed", "wrist", "knee", "balance"],
  revcrunch: ["bed", "flexion"],
  revlunge: ["standing", "knee", "hip", "balance"],
  heeltaps: ["bed", "flexion", "neck", "isometric"],
  bridgehold: ["bed", "hip", "isometric"],
  squatpulse: ["standing", "knee", "isometric"],
  pelvictilt: ["bed"],
  supinetwist: ["bed", "rotation"],
  breathing: ["bed", "breath"],
  legsup: ["bed"],
  happybaby: ["bed", "hip", "knee"],
  butterfly: ["bed", "hip", "knee", "flexion"],
  recbutterfly: ["bed", "hip"],
  sphinx: ["bed", "shoulder"],
  widechild: ["bed", "knee"],
  seatedfold: ["bed", "flexion", "hip"],
  fig4: ["bed", "hip"],
  wipers: ["bed", "rotation"],
  hipflexor: ["bed", "knee", "hip", "balance"],
  thread: ["bed", "knee", "shoulder", "rotation"],
  wallangels: ["wall", "shoulder"],
  childs: ["bed", "knee"],
  kneechest: ["bed", "hip"],
  chestopen: ["wall", "shoulder"],
};

/**
 * The conditions under which a move is not offered as written (`MoveFlag`), from the two 60+
 * reports (move-library §2.8, training-system-inventory §2.2) and the condition rules in
 * older-adults-guidelines §6. A flag is data: the template filler (ADR-052) decides whether that
 * means the `EASIER` move, the move with support, or nothing. `footSore`, `pregnant` and `recent`
 * are read off the tags by `moves.ts::derivedFlags` and never written here.
 *
 * Holds are not flagged `bp` for being holds: isometric work lowered resting pressure more than
 * any other kind of exercise (Edwards 2023), so the rule is "never hold the breath", which the
 * move's words carry. `bp` marks the head below the heart and effort that invites a held breath.
 */
export const FLAGS: Readonly<Record<ExerciseId, MoveFlag[]>> = {
  twists: ["osteoporosis"],
  catcow: ["knee", "osteoporosis"],
  hipcircles: ["balance"],
  march: ["balance"],
  squat: ["knee", "hipReplacement", "balance"],
  sideplank: ["bp", "knee"],
  bicycle: ["osteoporosis"],
  plank: ["bp"],
  stdoblique: ["balance"],
  marchdrive: ["balance"],
  boxing: ["knee", "balance"],
  woodchop: ["osteoporosis", "knee", "balance"],
  kneetwist: ["balance", "hipReplacement"],
  hollow: ["osteoporosis", "bp"],
  birddog: ["knee", "balance"],
  revcrunch: ["osteoporosis"],
  revlunge: ["knee", "hipReplacement", "balance"],
  heeltaps: ["osteoporosis"],
  bridgehold: ["bp"],
  squatpulse: ["knee", "hipReplacement", "balance"],
  supinetwist: ["hipReplacement", "osteoporosis"],
  legsup: ["bp"],
  happybaby: ["hipReplacement", "knee"],
  butterfly: ["knee", "hipReplacement", "osteoporosis"],
  widechild: ["knee", "bp"],
  seatedfold: ["osteoporosis", "bp"],
  fig4: ["hipReplacement", "knee"],
  hipflexor: ["knee", "balance", "hipReplacement"],
  thread: ["knee", "balance"],
  childs: ["knee", "bp"],
  kneechest: ["hipReplacement"],
};

/** The move to offer in place of one a flag or the 60+ verdict rules out. */
export const EASIER: Readonly<Record<ExerciseId, ExerciseId>> = {
  march: "seatmarch",
  steptouch: "sidewalk",
  squat: "sitstand",
  revlunge: "semitandem",
  inclinepush: "wallpush",
  bridgehold: "bridge",
  deadbug: "heelslide",
  sideplank: "wallside",
  birddog: "standbirddog",
  yraise: "wallslide",
  sphinx: "backext",
  hipflexor: "standhipflex",
  fig4: "seatfig4",
  butterfly: "recbutterfly",
  seatedfold: "hamchair",
  marchdrive: "march",
  boxing: "seatpunch",
  punchup: "seatpunch",
  kneetwist: "march",
  bicycle: "deadbug",
  revcrunch: "pelvictilt",
  heeltaps: "pelvictilt",
  hollow: "deadbug",
  stdoblique: "sidebend",
  plank: "wallplank",
  wallpushfast: "wallpush",
  squatpulse: "sitstand",
  woodchop: "trunkturn",
  happybaby: "kneechest",
  twists: "trunkturn",
  armcircles: "shroll",
  sidebend: "sidebendchair",
};

/** The next step up, where the library has one. */
export const HARDER: Readonly<Record<ExerciseId, ExerciseId>> = {
  wallpush: "inclinepush",
  inclinepush: "plank",
  bridge: "bridgehold",
  march: "marchdrive",
  breathing: "anulom",
  wallpushfast: "inclinepush",
  wallangels: "yraise",
};

/** One line on what each move is for, in the person's words; the sheet shows it under the how. */
export const WHY: Readonly<Record<ExerciseId, string>> = {
  armcircles: "so the shoulders move freely before they work",
  twists: "so the spine turns easily before the session asks more of it",
  catcow: "so the whole spine bends and straightens through its range",
  hipcircles: "so stiff hips loosen before you stand on them",
  march: "so the heart warms up and the legs lift cleanly",
  wallpush: "so pushing up from a chair or a bed stays easy",
  deadbug: "so the deep belly muscles hold the back still while the limbs move",
  inclinepush: "so the chest and arms grow strong enough to push you up from the floor",
  bridge: "so the buttocks lift and steady the hips and lower back",
  squat: "so sitting down and standing up stay strong",
  sideplank: "so the sides of the trunk hold you straight",
  bicycle: "so the waist muscles work together with the hips",
  plank: "so the whole trunk holds firm as one piece",
  stdoblique: "so the sides of the trunk work while you stand",
  yraise: "so the upper back holds the shoulders back and down",
  marchdrive: "so the heart works hard without any jumping",
  boxing: "so the heart and the waist work together",
  sidebend: "so the sides of the trunk stay long and supple",
  wallpushfast: "so the heart rate climbs while the arms work",
  woodchop: "so the trunk turns with power",
  kneetwist: "so the waist and the hip work together on one leg",
  punchup: "so the shoulders and the heart both get going",
  steptouch: "so the heart works with small, easy steps",
  hollow: "so the front of the trunk holds firm",
  birddog: "so the back stays steady while an arm and a leg reach",
  revcrunch: "so the lower belly muscles work",
  revlunge: "so each leg grows strong on its own",
  heeltaps: "so the sides of the waist work",
  bridgehold: "so the buttocks hold the hips up for longer",
  squatpulse: "so the thighs keep working without a rest",
  pelvictilt: "so a stiff lower back moves gently",
  supinetwist: "so the lower back and waist unwind",
  breathing: "so the breath slows and the belly relaxes",
  legsup: "so tired legs drain and the lower back rests",
  happybaby: "so the hips and inner thighs open",
  butterfly: "so the inner thighs and hips open",
  recbutterfly: "so the hips open with the bed taking your weight",
  sphinx: "so the lower back bends the other way after a day of sitting",
  widechild: "so the lower back and hips rest and lengthen",
  seatedfold: "so the backs of the legs lengthen",
  fig4: "so the buttock and the outside of the hip loosen",
  wipers: "so the lower back and hips loosen side to side",
  hipflexor: "so the front of the hip lengthens after sitting",
  thread: "so the upper back turns and the shoulder loosens",
  wallangels: "so the shoulders reach overhead with the back straight",
  childs: "so the back rests and lengthens at the end of a session",
  kneechest: "so the lower back and buttock ease",
  chestopen: "so the chest opens and the shoulders sit back",
};

/**
 * The move's name in Devanagari, for the moves a parent will meet (every one in the warm-up, the
 * cool-down and the Steady templates) and the named yoga. A gym move with no Hindi name of its
 * own — dead bug, plank, the crunches — is left out rather than given a made-up one.
 */
export const HI: Readonly<Record<ExerciseId, string>> = {
  armcircles: "हाथ घुमाना",
  twists: "कमर घुमाना",
  catcow: "मार्जरी-बिटिलासन",
  hipcircles: "कूल्हे घुमाना",
  march: "कदमताल",
  wallpush: "दीवार पर पुश-अप",
  bridge: "सेतु बंधासन",
  squat: "उठक-बैठक",
  marchdrive: "हाथ चलाते हुए कदमताल",
  boxing: "हवा में मुक्केबाज़ी",
  sidebend: "बगल में झुकना",
  steptouch: "दाएँ-बाएँ कदम",
  bridgehold: "सेतु बंधासन में रुकना",
  pelvictilt: "कमर बिस्तर से सटाना",
  supinetwist: "सुप्त मत्स्येन्द्रासन",
  breathing: "पेट से साँस",
  legsup: "पैर ऊपर रखकर आराम",
  happybaby: "आनंद बालासन",
  butterfly: "तितली आसन",
  recbutterfly: "सुप्त बद्ध कोणासन",
  sphinx: "सालंब भुजंगासन",
  widechild: "बालासन, घुटने खुले",
  seatedfold: "पश्चिमोत्तानासन",
  wipers: "घुटने दाएँ-बाएँ गिराना",
  wallangels: "दीवार पर हाथ ऊपर-नीचे",
  childs: "बालासन",
  kneechest: "अर्ध पवनमुक्तासन",
  chestopen: "छाती खोलना",
};

/**
 * The asana's name, only where the move is that pose and not merely like it (plan §3.7): the
 * sphinx is Salamba Bhujangasana (the forearm cobra), knee-to-chest one leg at a time is Ardha
 * Pawanmuktasana. Belly breathing is not a pranayama, and legs on a pillow are not Viparita Karani.
 */
export const YOGA: Readonly<Record<ExerciseId, string>> = {
  catcow: "Marjaryasana–Bitilasana",
  bridge: "Setu Bandhasana",
  bridgehold: "Setu Bandhasana",
  supinetwist: "Supta Matsyendrasana",
  happybaby: "Ananda Balasana",
  butterfly: "Baddha Konasana",
  recbutterfly: "Supta Baddha Konasana",
  sphinx: "Salamba Bhujangasana",
  seatedfold: "Paschimottanasana",
  childs: "Balasana",
  widechild: "Balasana",
  kneechest: "Ardha Pawanmuktasana",
};

const OTAGO =
  "https://www.med.unc.edu/aging/cgwep/courses/otago-exercise-program/otago-videos/balance-exercises/";
const NHS_BALANCE = "https://www.nhs.uk/live-well/exercise/strength-and-flexibility-exercises/balance-exercises/";

/**
 * A fixed video or picture sequence for the ten moves that are unsafe to learn from words alone
 * (move-library §5): the balance walks, the step-up, the wall sit, sit-to-stand, the pendulum and
 * the curl-up. Each is an NHS page or the Otago programme's own clip (UNC's CGWEP), linked rather
 * than embedded — a link is not redistribution. Every other move keeps its YouTube search. All ten
 * are library moves; the table sits here beside the others so every link is in one place.
 */
export const VIDEO: Readonly<Record<ExerciseId, string>> = {
  sitstand: OTAGO + "sit-to-stand/",
  onelegstand: OTAGO + "one-leg-stand/",
  tandemwalk: OTAGO + "heel-toe-walk/",
  backwalk: OTAGO + "backwards-walk/",
  figure8: OTAGO + "walk-and-turn/",
  grapevine: NHS_BALANCE,
  stepup: NHS_BALANCE,
  wallsit: "https://www.mskdorset.nhs.uk/knee-pain/knee-pain-anterior-knee-pain-akp/",
  pendulum: "https://www.southtees.nhs.uk/resources/pendular/",
  curlup: "https://www.cht.nhs.uk/services/clinical-services/physiotherapy-outpatients/the-low-back/pilates",
};

/**
 * Whether each of the 48 suits a 60+ beginner: `ok` as written, `easier` with a regression (the
 * `EASIER` move or the `VERDICT_WHY` line), `out` not offered. Two reports judged them
 * (move-library §2.8, training-system-inventory §2.2); where they differ the stricter verdict
 * stands, so the inventory's four "do not offer" and the library's loaded flexion, speed,
 * breath-held plank and happy baby are all `out`, and a move only one report wanted regressed is
 * `easier`.
 */
export type Verdict60 = "ok" | "easier" | "out";

export const VERDICT_60: Readonly<Record<ExerciseId, Verdict60>> = {
  armcircles: "ok",
  twists: "ok",
  hipcircles: "ok",
  wallpush: "ok",
  bridge: "ok",
  sidebend: "ok",
  pelvictilt: "ok",
  breathing: "ok",
  legsup: "ok",
  recbutterfly: "ok",
  wipers: "ok",
  wallangels: "ok",
  kneechest: "ok",
  chestopen: "ok",

  catcow: "easier",
  march: "easier",
  deadbug: "easier",
  inclinepush: "easier",
  squat: "easier",
  sideplank: "easier",
  yraise: "easier",
  marchdrive: "easier",
  boxing: "easier",
  kneetwist: "easier",
  punchup: "easier",
  steptouch: "easier",
  birddog: "easier",
  bridgehold: "easier",
  supinetwist: "easier",
  butterfly: "easier",
  sphinx: "easier",
  widechild: "easier",
  seatedfold: "easier",
  fig4: "easier",
  hipflexor: "easier",
  thread: "easier",
  childs: "easier",

  bicycle: "out",
  revcrunch: "out",
  heeltaps: "out",
  hollow: "out",
  stdoblique: "out",
  plank: "out",
  wallpushfast: "out",
  squatpulse: "out",
  woodchop: "out",
  happybaby: "out",
  revlunge: "out",
};

/** Why a move is regressed or left out at 60+, in one line the library can show. */
export const VERDICT_WHY: Readonly<Record<ExerciseId, string>> = {
  catcow: "On hands and knees is hard on sore knees and wrists — do it sitting on a chair",
  march: "Hold the counter, knees lower; sit for it on a tired day",
  deadbug: "Arms only to start, or heel slides",
  inclinepush: "Hands on the kitchen counter or the wall rather than a low bed",
  squat: "Sit-to-stand from a chair until the counter-held squat is easy",
  sideplank: "Lean on the wall instead, breathing all the way",
  yraise: "Lying face down is hard to reach — slide the arms up a wall",
  marchdrive: "Chair beside you, knees lower; seated first",
  boxing: "Flat feet, no bounce; seated first",
  kneetwist: "A small knee lift holding the counter, no twist",
  punchup: "Slower and seated first; below head height for a sore shoulder",
  steptouch: "Small steps along the counter",
  birddog: "Hands and knees is hard on knees and wrists — stand and hold the counter",
  bridgehold: "Turn it into bridge lifts, holds of 15 s or less, counting aloud",
  supinetwist: "A small range, knees together",
  butterfly: "Long sitting on a bed is hard — lie back with pillows under the knees",
  sphinx: "Only if getting onto the front is easy; seated back extension otherwise",
  widechild: "Deep knee bending — pillows under the hips, or a seated forward lean",
  seatedfold: "Rounding forward loads the spine — the seated hamstring stretch hinges instead",
  fig4: "On a chair; not after a hip replacement",
  hipflexor: "Kneeling is hard on knees and balance — stretch standing at the counter",
  thread: "Hands and knees is hard on knees — a seated turn instead",
  childs: "Deep knee bending — pillows between calves and thighs, or skip it",
  bicycle: "Bending and twisting the spine under load — not for bones after menopause",
  revcrunch: "Curling the lower spine under load — pelvic tilts instead",
  heeltaps: "A held crunch with the head lifted — pelvic tilts instead",
  hollow: "A held crunch that invites holding the breath",
  stdoblique: "Hands behind the head on one leg — too much balance with no hand free",
  plank: "Getting down, the wrists and a held breath — the wall plank instead",
  wallpushfast: "Speed adds nothing here — wall push-ups at an easy tempo",
  squatpulse: "Knees held bent under load — sit-to-stand instead",
  woodchop: "Twisting and bending at speed with a pivoting knee",
  happybaby: "Deep hip bending, out after a hip replacement — knee-to-chest instead",
  revlunge: "A dynamic one-leg drop for knees and balance — a staggered stand, then sit-to-stand",
};

/** The 60+ verdict for a move; a library move is written for this age and is always `ok`. */
export function verdict60(id: ExerciseId): Verdict60 {
  return VERDICT_60[id] ?? "ok";
}
